import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';
import {
  IEcommerceProductHierarchy,
  IEcommerceProductHierarchyItem,
  IEcommerceSubBrandHierarchy,
  IProductStage
} from 'src/models/ecommerce';
import {
  IAdvanceSearchSocialMedia,
  IDateRange,
  ILabel,
  TContentType,
  TPostMadeType,
  TPostOwnership,
  TSentiment,
  TValidation
} from 'src/models/general';
import defaultDateRange from 'src/utils/defaultDateRange';

export interface IAdvanceSearchEcommerce
  extends Omit<IAdvanceSearchSocialMedia, 'selectedOriginalToPost'> {
  selectedOriginalToPost?: IEcommerceProductHierarchyItem[];
  selectedBrand?: IEcommerceProductHierarchy[];
  selectedSubBrand?: IEcommerceSubBrandHierarchy[];
  selectedLabel?: ILabel[];
  selectedProductStages?: IProductStage[];
}

export const initialState: IAdvanceSearchEcommerce = {
  sentiment: [],
  postOwnership: [],
  contentTypeList: [],
  validationList: [],
  labelList: [],
  excludeLabelList: [],
  includeKeywords: [],
  excludeKeywords: [],
  fromUsername: '',
  replyToOriginalId: [],
  showEmptyLabel: false,
  sentimentConfidenceLevelMin: 0,
  sentimentConfidenceLevelMax: 1,
  streamsCommentingToPost: {
    result: [],
    totalRows: 0,
    totalPages: 0,
    page: 1,
    limit: 20
  },
  selectedOriginalToPost: [],
  queryStringBooleanOperator: 'and',
  languageList: [],
  userRating: [],
  emotion: [],
  validationEmotionList: [],
  selectAllPrimaryPost: false, // for commenting to post
  retweetFilter: 'all',
  talkType: [],
  ageGeneration: [],
  postMadeType: 'talk',
  commentingToPostDateRange: {
    start: moment(defaultDateRange().startDate).toDate(),
    end: moment(defaultDateRange().endDate).toDate()
  },
  commentingToPostLabelList: [],
  selectedBrand: [],
  selectedSubBrand: [],
  selectedLabel: [],
  selectedProductStages: []
};

export const advanceSearchEcommerce = createSlice({
  name: 'advanceSearchEcommerce',
  initialState,
  reducers: {
    reducerUpdateAdvanceSearchSentiment: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<Array<TSentiment>>
    ) => {
      state.sentiment = action.payload;
    },
    reducerUpdateAdvanceSearchPostOwnership: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<Array<TPostOwnership>>
    ) => {
      state.postOwnership = action.payload;
    },
    reducerUpdateAdvanceSearchContentTypeList: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<Array<TContentType>>
    ) => {
      state.contentTypeList = action.payload;
    },
    reducerUpdateAdvanceSearchValidationList: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<Array<TValidation>>
    ) => {
      state.validationList = action.payload;
    },
    reducerUpdateAdvanceSearchLabelList: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<Array<string>>
    ) => {
      state.labelList = action.payload;
    },
    reducerUpdateAdvanceSearchIncludeKeywords: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<Array<string>>
    ) => {
      state.includeKeywords = action.payload;
    },
    reducerUpdateAdvanceSearchExcludeKeywords: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<Array<string>>
    ) => {
      state.excludeKeywords = action.payload;
    },
    reducerUpdateAdvanceSearchFromUsername: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<string>
    ) => {
      state.fromUsername = action.payload;
    },
    reducerUpdateAdvanceSearchReplyToOriginalId: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<Array<string>>
    ) => {
      state.replyToOriginalId = action.payload;
    },
    reducerUpdateAdvanceSearchShowEmptyLabel: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<boolean>
    ) => {
      state.showEmptyLabel = action.payload;
    },
    reducerUpdateAdvanceSearchSentimentConfidenceLevelMin: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<number>
    ) => {
      state.sentimentConfidenceLevelMin = action.payload;
    },
    reducerUpdateAdvanceSearchSentimentConfidenceLevelMax: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<number>
    ) => {
      state.sentimentConfidenceLevelMax = action.payload;
    },
    reducerUpdateSelectAllPrimaryPost: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<boolean>
    ) => {
      state.selectAllPrimaryPost = action.payload;
    },
    reducerUpdatePostMadeType: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<TPostMadeType>
    ) => {
      state.postMadeType = action.payload;
    },
    reducerUpdateCommentingToPostDateRange: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<IDateRange>
    ) => {
      state.commentingToPostDateRange = action.payload;
    },
    reducerUpdateCommentingToPostLabelList: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<Array<ILabel>>
    ) => {
      state.commentingToPostLabelList = action.payload;
    },
    reducerUpdateAdvanceSearch: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<IAdvanceSearchEcommerce>
    ) => {
      state.includeKeywords = action.payload.includeKeywords;
      state.excludeKeywords = action.payload.excludeKeywords;
      state.fromUsername = action.payload.fromUsername;
      state.sentiment = action.payload.sentiment;
      state.postOwnership = action.payload.postOwnership;
      state.validationList = action.payload.validationList;
      state.contentTypeList = action.payload.contentTypeList;
      state.labelList = action.payload.labelList;
      state.excludeLabelList = action.payload.excludeLabelList;
      state.sentimentConfidenceLevelMin =
        action.payload.sentimentConfidenceLevelMin;
      state.sentimentConfidenceLevelMax =
        action.payload.sentimentConfidenceLevelMax;
      state.showEmptyLabel = action.payload.showEmptyLabel;
      state.replyToOriginalId = action.payload.replyToOriginalId;
      state.streamsCommentingToPost = action.payload.streamsCommentingToPost;
      state.selectedOriginalToPost = action.payload.selectedOriginalToPost;
      state.selectedBrand = action.payload.selectedBrand;
      state.selectedSubBrand = action.payload.selectedSubBrand;
      state.selectedLabel = action.payload.selectedLabel;
      state.selectedProductStages = action.payload.selectedProductStages;
      state.searchOriginalToPost = action.payload.searchOriginalToPost;
      state.queryStringBooleanOperator =
        action.payload.queryStringBooleanOperator;
      state.languageList = action.payload.languageList;
      state.userRating = action.payload.userRating;
      state.emotion = action.payload.emotion;
      state.validationEmotionList = action.payload.validationEmotionList;
      state.selectAllPrimaryPost = action.payload.selectAllPrimaryPost;
      state.retweetFilter = action.payload.retweetFilter;
      state.talkType = action.payload.talkType;
      state.ageGeneration = action.payload.ageGeneration;
      state.postMadeType = action.payload.postMadeType;
    },
    reducerUpdateAdvanceClear: (
      state: IAdvanceSearchEcommerce,
      action: PayloadAction<IAdvanceSearchEcommerce>
    ) => {
      state.includeKeywords = action.payload.includeKeywords;
      state.excludeKeywords = action.payload.excludeKeywords;
      state.fromUsername = action.payload.fromUsername;
      state.sentiment = action.payload.sentiment;
      state.postOwnership = action.payload.postOwnership;
      state.validationList = action.payload.validationList;
      state.contentTypeList = action.payload.contentTypeList;
      state.labelList = action.payload.labelList;
      state.excludeLabelList = action.payload.excludeLabelList;
      state.sentimentConfidenceLevelMin =
        action.payload.sentimentConfidenceLevelMin;
      state.sentimentConfidenceLevelMax =
        action.payload.sentimentConfidenceLevelMax;
      state.showEmptyLabel = action.payload.showEmptyLabel;
      state.replyToOriginalId = action.payload.replyToOriginalId;
      state.streamsCommentingToPost = action.payload.streamsCommentingToPost;
      state.selectedOriginalToPost = action.payload.selectedOriginalToPost;
      state.selectedBrand = action.payload.selectedBrand;
      state.selectedSubBrand = action.payload.selectedSubBrand;
      state.selectedLabel = action.payload.selectedLabel;
      state.selectedProductStages = action.payload.selectedProductStages;
      state.searchOriginalToPost = action.payload.searchOriginalToPost;
      state.queryStringBooleanOperator =
        action.payload.queryStringBooleanOperator;
      state.languageList = action.payload.languageList;
      state.userRating = action.payload.userRating;
      state.emotion = action.payload.emotion;
      state.validationEmotionList = action.payload.validationEmotionList;
      state.retweetFilter = action.payload.retweetFilter;
      state.talkType = action.payload.talkType;
      state.ageGeneration = action.payload.ageGeneration;
      state.postMadeType = action.payload.postMadeType;
      state.selectAllPrimaryPost = action.payload.selectAllPrimaryPost;
      state.commentingToPostLabelList =
        action.payload.commentingToPostLabelList;
    }
  }
});

export const {
  reducerUpdateAdvanceSearch,
  reducerUpdateAdvanceSearchSentiment,
  reducerUpdateAdvanceSearchContentTypeList,
  reducerUpdateAdvanceSearchExcludeKeywords,
  reducerUpdateAdvanceSearchFromUsername,
  reducerUpdateAdvanceSearchIncludeKeywords,
  reducerUpdateAdvanceSearchLabelList,
  reducerUpdateAdvanceSearchPostOwnership,
  reducerUpdateAdvanceSearchReplyToOriginalId,
  reducerUpdateAdvanceSearchSentimentConfidenceLevelMax,
  reducerUpdateAdvanceSearchSentimentConfidenceLevelMin,
  reducerUpdateAdvanceSearchShowEmptyLabel,
  reducerUpdateAdvanceSearchValidationList,
  reducerUpdateAdvanceClear,
  reducerUpdateSelectAllPrimaryPost,
  reducerUpdatePostMadeType,
  reducerUpdateCommentingToPostDateRange,
  reducerUpdateCommentingToPostLabelList
} = advanceSearchEcommerce.actions;

export default advanceSearchEcommerce.reducer;
